import classnames from 'classnames';
import { uniqBy } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { Dispatch } from 'redux';
import { PrimaryButton } from '../../../common/buttons/PrimaryButton/PrimaryButton';
import FlatMenu from '../../../common/components/FlatMenu/FlatMenu';
import { OnClickElsewhere } from '../../../common/components/OnClickElsewhere/OnClickElsewhere';
import Spinner from '../../../common/components/Spinner';
import * as allPermissions from '@mm/constants/facebook-permissions';
import { CHATBOT_HOME } from '../../../common/constants/routes';
import { ErrorReportingUtils } from '@mm/plugins';
import { trackWebAppGAEvent } from '../../../common/helpers/plugins/website-google-analytics';
import { useLockBodyScroll } from '../../../common/hooks/use-body-lock-scroll';
import { useInfiniteScroll } from '../../../common/hooks/use-infinite-scroll';
import { useResolutionInfo, useDebounce } from '@mm/hooks';
import { useChatbotCRUDActions } from '@mm/state/actions/ChatbotCRUDActions';
import { useConnectedPageCRUDActions } from '@mm/state/actions/ConnectedPageCRUDActions';
import facebookSDKActions from '@mm/state/actions/facebookActions';
import { useFacebookSessionCRUDActions } from '@mm/state/actions/FacebookSessionCRUDActions';
import { useSubscriptionCRUDActions } from '@mm/state/actions/SubscriptionCRUDActions';
import { useUsersCRUDActions } from '@mm/state/actions/UsersCRUDActions';
import {
  BasicPage,
  EnhancedBillingPage,
} from '@mm/state/interfaces/connected-page';
import { Plan, PlansMapping } from '@mm/state/interfaces/subscription';
import CompanyModel from '@mm/state/model/CompanyModel';
import ConnectedPageModel from '@mm/state/model/ConnectedPageModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import {
  activeFacebookPageSelector,
  getEnhancedBillingPages,
} from '@mm/state/selectors/facebookPageSelector';
import {
  isFacebookConnectedSelector,
  selectCurrentUser,
} from '@mm/state/selectors/userSelector';
import {
  activeCompanySelector,
  selectCompanies,
} from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import './ActivePageSlideMenu.scss';
import { FacebookPageCard } from './FacebookPageCard/FacebookPageCard';
import { FacebookPageCustomizationPane } from './FacebookPageCustomizationPane/FacebookPageCustomizationPane';
import { PagesListHeader } from './PagesListHeader/PagesListHeader';
import { SlideoutMenu } from '../../../editor/components/SlideoutMenu/components/SlideoutMenu';
import { useViewActions } from '@mm/state/actions/viewActions';
import flagIcon from 'assets/flag-icon.svg';
import { CloseOutlined, LeftOutlined } from '@ant-design/icons';
import { SearchInput } from '../../../common/components/SearchInput/SearchInput';

const FETCH_PAGE_SIZE = 25;

type Props = {
  onPageSelect?: (page: BasicPage | ConnectedPageModel) => void;
};

type PageDataItem = {
  query: string;
  facebookPages: EnhancedBillingPage[];
  nextPage: number;
  lastPageFetched: boolean;
  fetchInProgress: boolean;
};
type PlanDataItem = {
  plans: Plan[];
  plansMapping?: PlansMapping;
};
export type FollowConvosItem = {
  followUpNumber: FollowUps[];
};
type FollowUps = {
  [key: string]: string | string[];
};

export const useFacebookLogin = () => {
  const dispatch: Dispatch<StoreState> = useDispatch();

  const permissions = allPermissions.getArrayOfPermissions(
    allPermissions.ALL_PERMISSIONS,
  );

  const login = async () => {
    try {
      const res = await dispatch(
        facebookSDKActions.requestPermissionsWithManualFlowFallback(
          permissions,
        ),
      );
      if (res) {
        trackWebAppGAEvent('login_before_connect_page', {
          category: 'login',
          label: 'login_success',
        });

        return res;
      }
    } catch (error) {
      ErrorReportingUtils.reportError(`Active Page Slide Menu: login failure`, {
        extraData: { permissionsRequested: permissions, error },
      });
      trackWebAppGAEvent('login_before_connect_page', {
        category: 'login',
        label: 'login_fail',
      });
      throw error;
    }
  };

  return { login };
};

// tslint:disable-next-line:cyclomatic-complexity
export const ActivePageSlideMenuContent = ({
  onPageSelect = () => {},
}: Props) => {
  const history = useHistory();

  const companies = useSelector(selectCompanies);
  const [companySearchInput, setCompanySearchInput] = useState('');
  const debouncedCompanySearchInput = useDebounce(companySearchInput, 500);
  const filteredCompanies = companies.filter(c =>
    c.name
      .toLocaleLowerCase()
      .includes(debouncedCompanySearchInput.toLocaleLowerCase()),
  );
  const session = useSelector((state: StoreState) => state.session.session);
  const activeCompany = useSelector(activeCompanySelector);
  const activeFacebookPage = useSelector(activeFacebookPageSelector);
  const isFacebookConnected = useSelector(isFacebookConnectedSelector);
  const currentUser = useSelector(selectCurrentUser);
  const modalActions = useModalActions();

  const facebookSessionCRUDActions = useFacebookSessionCRUDActions();
  const { updateActiveCompanyId: activateCompany } = useUsersCRUDActions();
  const { getPlans } = useSubscriptionCRUDActions();
  const {
    retrievePage: fetchPage,
    fetchPagesForCompany,
    fetchFollowUpConversations,
  } = useConnectedPageCRUDActions();
  const { retrieveAll: retrieveChatbotsByPage } = useChatbotCRUDActions();
  const { openGeneralConfirmModal, openCreateBotModal } = useModalActions();
  const usersCRUDActions = useUsersCRUDActions();

  const { login } = useFacebookLogin();
  const { isMobile, isTablet } = useResolutionInfo();
  useLockBodyScroll(isTablet || isMobile);

  const promptCreateBotForPage = async (
    companyId: number,
    page: BasicPage | ConnectedPageModel,
  ) =>
    openGeneralConfirmModal({
      headerText: 'Facebook Page has no chatbots',
      messageText: `The Facebook Page "${page.name}" has no chatbots.  Would you like to create one?`,
      cancelButtonFirst: true,
      okButtonText: 'Create chatbot',
      okButtonStyling: { blue: true },
      onCloseHandler: ok => {
        if (ok) {
          openCreateBotModal(companyId, page.id, true);
        }
      },
    });

  const [searchInput, setSearchInput] = useState('');
  const debouncedSearchInput = useDebounce(searchInput, 500);
  const [selectedCompanyId, setSelectedCompanyId] = useState<number>();
  const planData = useRef({} as Record<number, PlanDataItem | undefined>);
  const [pageData, setPageData] = useState(
    {} as Record<number, PageDataItem | undefined>,
  );
  const [followUpConvos, setFollowUpConvos] = useState(
    {} as Record<number, FollowConvosItem | undefined>,
  );
  const [userRoleInCompany, setUserRoleInCompany] = useState('');
  const [facebookPageUnderEdit, setFacebookPageUnderEdit] =
    useState<EnhancedBillingPage>();

  const selectedCompany = companies.find(c => c.id === selectedCompanyId);

  const { scrollContainerRef } = useInfiniteScroll<HTMLDivElement>({
    fetchDisabled: !!(
      selectedCompanyId && pageData[selectedCompanyId]?.lastPageFetched
    ),
    fetchMore: async () => {
      if (!isMobile && selectedCompanyId) {
        await loadPages(selectedCompanyId);
      }
    },
  });

  useEffect(() => {
    (async () => {
      setPageData({});
      planData.current = {};
      if (activeCompany) {
        setSelectedCompanyId(companyId => companyId || activeCompany.id);
        await loadPlans(selectedCompanyId || activeCompany.id);
        await loadPages(selectedCompanyId || activeCompany.id, true);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCompany, activeFacebookPage?.remoteId]);

  useEffect(() => {
    if (selectedCompany?.id) {
      loadPages(selectedCompany.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchInput]);

  const setFetchInProgress = (companyId: number, fetchInProgress: boolean) => {
    setPageData(pd => {
      const existingData = pd[companyId];
      if (!existingData) {
        return pd;
      }
      return {
        ...pd,
        [companyId]: { ...existingData, fetchInProgress },
      };
    });
  };

  const isFetchingCompanyList =
    !selectedCompanyId ||
    !pageData[selectedCompanyId] ||
    pageData[selectedCompanyId]?.fetchInProgress;
  const plans =
    (selectedCompanyId && planData.current[selectedCompanyId]?.plans) || [];
  const facebookPages = (() => {
    if (!selectedCompanyId) {
      return;
    }
    const pageDatum = pageData[selectedCompanyId];
    if (!pageDatum || pageDatum.query !== debouncedSearchInput) {
      return;
    }
    return pageDatum.facebookPages;
  })();

  const loadPlans = async (companyId: number) => {
    const plansResponse = await getPlans(companyId);
    if (plansResponse) {
      const { plans, plansMapping } = plansResponse;
      planData.current[companyId] = {
        plans,
        plansMapping,
      };
    }
  };

  const loadFollowUps = async (companyId: number) => {
    const followupsResponse = await fetchFollowUpConversations(companyId);
    if (followupsResponse) {
      const { followUpNumber } = followupsResponse;
      setFollowUpConvos(followUpNumber);
      localStorage.setItem('followupNumber', JSON.stringify(followUpNumber));
    }
  };

  const retrieveUserRole = async (companyId: number, userId: number) => {
    const res = await usersCRUDActions.retrieveById(companyId, userId);
    const { roleCode } = res;
    setUserRoleInCompany(roleCode || '');
  };

  useEffect(() => {
    if (selectedCompanyId) {
      loadFollowUps(selectedCompanyId);
      if (currentUser) {
        retrieveUserRole(selectedCompanyId, currentUser.id);
      }
    }
  }, [selectedCompanyId, currentUser]);

  const loadPages = async (companyId: number, reset?: boolean) => {
    try {
      setFetchInProgress(companyId, true);

      const {
        lastPageFetched,
        nextPage,
        query: previousQuery,
      } = pageData[companyId] || {};

      if (!reset && previousQuery === debouncedSearchInput && lastPageFetched) {
        return;
      }

      const { plans, plansMapping } = planData.current[companyId] || {};
      if (!plans) {
        return;
      }
      const pageResponse = await fetchPagesForCompany({
        companyId,
        pageNumber:
          !reset && previousQuery === debouncedSearchInput ? nextPage || 0 : 0,
        perPage: FETCH_PAGE_SIZE,
        searchQuery: debouncedSearchInput,
        withSubscription: true,
        withFacebookErrors: true,
        showInstagramAccounts: true,
        showActivated: true,
      });
      if (!pageResponse) {
        return;
      }
      const newPages = getEnhancedBillingPages(
        pageResponse.facebookPages as BasicPage[],
        plans,
        plansMapping,
      );
      setPageData(pd => {
        const oldData = pd[companyId];

        const facebookPages =
          reset || !oldData || oldData.query !== debouncedSearchInput
            ? newPages
            : uniqBy([...oldData.facebookPages, ...newPages], fp => fp.id);

        return {
          ...pd,
          [companyId]: {
            query: debouncedSearchInput,
            facebookPages,
            nextPage: pageResponse.pagination.page + 1,
            fetchInProgress: false,
            lastPageFetched: pageResponse.pagination.isLastPage,
          },
        };
      });
    } finally {
      setFetchInProgress(companyId, false);
    }
  };

  const changeCompany = async (companyId: number) => {
    if (!planData.current[companyId]) {
      setFetchInProgress(companyId, true);
      await loadPlans(companyId);
    }
    loadPages(companyId);
  };

  const resetStateOnClose = () => {
    viewActions.hideSlideoutMenu();
  };

  const [showPagesSelector, setShowPagesSelector] = useState(false);

  const onCompanySelect = async (nextSelectedCompany: CompanyModel) => {
    setSelectedCompanyId(nextSelectedCompany.id);
    changeCompany(nextSelectedCompany.id);
    if (isMobile) {
      setShowPagesSelector(true);
    }
    scrollContainerRef.current?.scrollTo?.({ top: 0 });
  };

  const viewActions = useViewActions();
  const onFacebookPageSelect = async (
    facebookPage: BasicPage | ConnectedPageModel,
  ) => {
    onPageSelect(facebookPage);

    let path = '';

    if (facebookPage.activeBotId) {
      if (
        activeCompany &&
        selectedCompany &&
        activeCompany.id !== selectedCompany.id
      ) {
        await activateCompany(session.id, selectedCompany.id);
      }
      fetchPage(facebookPage.id);
      path = CHATBOT_HOME(facebookPage.activeBotId);
      history.push(path);
    } else {
      const bots = await retrieveChatbotsByPage(facebookPage.id);

      if (bots && bots.length) {
        if (
          activeCompany &&
          selectedCompany &&
          activeCompany.id !== selectedCompany.id
        ) {
          await activateCompany(session.id, selectedCompany.id);
        }
        fetchPage(facebookPage.id);
        path = CHATBOT_HOME(bots[0].id);
        history.push(path);
      } else if (selectedCompany) {
        promptCreateBotForPage(selectedCompany.id, facebookPage);
      }
    }

    viewActions.hideSlideoutMenu();
  };

  const classList = classnames('page-selector-pane', {
    'page-selector-pane--active': true,
    'page-selector-pane--is-mobile': isTablet || isMobile,
    'page-selector-pane--no-padding': facebookPageUnderEdit,
  });

  const websiteFormatted = selectedCompany?.website;

  const onConnectPageClick = async () => {
    if (!isFacebookConnected) {
      const session = await login();
      if (session && selectedCompany) {
        await facebookSessionCRUDActions.create(session);
        return modalActions.openConnectPagesModal({
          title: `Connect Facebook Page to ${
            websiteFormatted || 'your website'
          }`,
          company: selectedCompany,
          destinationPageId: activeFacebookPage?.id,
        });
      }
    }

    if (selectedCompany) {
      modalActions.openConnectPagesModal({ company: selectedCompany });
    }
  };

  if (!selectedCompany) {
    return null;
  }

  return (
    <OnClickElsewhere handleClick={resetStateOnClose} className={classList}>
      {facebookPageUnderEdit && (
        <FacebookPageCustomizationPane
          facebookPage={facebookPageUnderEdit}
          onClose={options => {
            if (options?.updatedProfilePicture) {
              loadPages(facebookPageUnderEdit.companyId, true);
            }
            setFacebookPageUnderEdit(undefined);
          }}
        />
      )}

      <div
        className="page-selector-pane__header"
        style={
          // We use CSS visibility here to avoid changing scroll while we are customizing a FB page
          { visibility: facebookPageUnderEdit ? 'hidden' : 'visible' }
        }
      >
        {isMobile && showPagesSelector && (
          <div
            className="page-selector-pane__header-back-menu"
            onClick={() => setShowPagesSelector(false)}
          >
            <LeftOutlined className="flat-menu-item__right-arrow" />
            Companies
          </div>
        )}

        {!isMobile && (
          <CloseOutlined
            onClick={() => resetStateOnClose()}
            className="page-selector-pane__header-close-icon"
          />
        )}
      </div>

      <div
        className="page-selector-pane__body"
        style={
          // We use CSS visibility here to avoid changing scroll while we are customizing a FB page
          { visibility: facebookPageUnderEdit ? 'hidden' : 'visible' }
        }
      >
        {(!isMobile || (isMobile && !showPagesSelector)) && (
          <div className="page-selector-pane__left-area">
            <div className="page-selector-pane__title">Companies</div>
            <div className="page-selector-pane__company-selector-wrapper">
              <SearchInput
                className={classnames(
                  'page-selector-pane__company-search-input',
                  'page-selector-pane__search-input',
                )}
                value={companySearchInput}
                onChange={e => setCompanySearchInput(e.target.value)}
                placeholder="Search Company"
                inputClassName="page-selector-pane__search-input--input"
                useDarkStyle
              />
              <FlatMenu
                activeItem={selectedCompany}
                items={filteredCompanies}
                onItemSelect={onCompanySelect}
                className="page-selector-pane__heading"
                itemClassName={
                  isMobile
                    ? 'page-selector-pane__menu-item-mobile'
                    : 'page-selector-pane__menu-item'
                }
                withRightArrow={isMobile}
              />
            </div>
          </div>
        )}

        {(!isMobile || (isMobile && showPagesSelector)) && (
          <div className="page-selector-pane__right-area">
            <PagesListHeader
              searchInput={searchInput}
              selectedCompany={selectedCompany}
              openConnectPagesModal={onConnectPageClick}
              setSearchInput={setSearchInput}
            />

            <div
              ref={scrollContainerRef}
              className="page-selector-pane__pages-list"
            >
              {!isFetchingCompanyList &&
                facebookPages &&
                !facebookPages.length && (
                  <>
                    <img
                      src={flagIcon}
                      className="page-selector-pane__flag-icon"
                    />

                    <div className="page-selector-pane__no-page-text">
                      {searchInput ? (
                        <span>
                          No accounts match search: {debouncedSearchInput}
                        </span>
                      ) : (
                        <span>No accounts connected to this company</span>
                      )}
                    </div>
                  </>
                )}

              {facebookPages?.map(facebookPage => (
                <FacebookPageCard
                  company={selectedCompany}
                  facebookPage={facebookPage}
                  key={facebookPage.id}
                  onClick={() => onFacebookPageSelect(facebookPage)}
                  active={facebookPage.id === activeFacebookPage?.id}
                  plans={plans}
                  onCustomize={() => setFacebookPageUnderEdit(facebookPage)}
                  followUpConvoCount={
                    followUpConvos[facebookPage.remoteId] || null
                  }
                  userRoleInCompany={userRoleInCompany}
                />
              ))}
              {isMobile &&
                facebookPages &&
                !isFetchingCompanyList &&
                selectedCompanyId &&
                pageData[selectedCompanyId]?.lastPageFetched === false && (
                  <div className="page-selector-pane__mobile-load-more-button-container">
                    <PrimaryButton
                      className="page-selector-pane__mobile-load-more-button"
                      size="lg"
                      onClick={() => loadPages(selectedCompanyId)}
                    >
                      Load more...
                    </PrimaryButton>
                  </div>
                )}
              {(!facebookPages || isFetchingCompanyList) && (
                <Spinner
                  show={true}
                  size={40}
                  position="relative"
                  backgroundColor="transparent"
                  className="page-selector-pane__spinner"
                />
              )}
            </div>
          </div>
        )}
      </div>
    </OnClickElsewhere>
  );
};

export const ActivePageSlideMenu = (props: Props) => {
  return (
    <SlideoutMenu className="active-page-slide-menu">
      <SlideoutMenu.Content>
        <ActivePageSlideMenuContent {...props} />
      </SlideoutMenu.Content>
    </SlideoutMenu>
  );
};
